import { ActivatedRoute } from '@angular/router';

import { IdUtils } from '@ecm/consumer';
import { EcmDispatcherModule, EcmUrlSanitizerUtil, EnvUtils } from '@ecm/dispatcher';
import { BrandPicto } from '@ecm/material';
import { Contract, PortfolioService, UrlHelper as UrlHelperPortfolio } from '@ecm/portfolio';
import { KenobiParams, KenobiService, Status, TMSService, TrackableView } from '@ecm/tracker';

import { ErrorPageService } from '../../services/error-page.service';
import { RouterService } from '../../services/router.service';
import { UrlHelper } from '../../utils/url-helper';
import { SelectorIdTrackingEnum } from './shared/selector-id-tracking.enum';
import { SelectorUniverseEnum } from './shared/selector-universe.enum';

export abstract class AbstractSelectorComponent implements TrackableView {
    /**
     * List of contracts group by brand, first group is current brand
     * @type {Map<string, Contract[]>}
     */
    public contractsByBrand: Map<string, Contract[]>;

    /**
     * List of available brands
     * @type {string[]}
     */
    public brandsAvailable: string[] = [];

    public isWebviewContext: boolean;

    /**
     * Url of reverse brand logo
     * @type {string}
     */
    public reverseBrandLogoUrl: string;

    /**
     * Universe parameter
     * @type {SelectorUniverseEnum}
     */
    public universe: SelectorUniverseEnum;

    /**
     * Id tracking GA4 parameter
     * @type {SelectorIdTrackingEnum}
     */
    public idTracking: SelectorIdTrackingEnum;

    /**
     * Sort contracts by brand, place in first current brand
     * @param {Contract[]} contracts
     * @param {boolean} isSosh
     * @returns {Contract[]}
     */
    private static sortContractsByBrand(contracts: Contract[], isSosh: boolean): Contract[] {
        return contracts.sort((a: Contract, b: Contract) => {
            if (a.brand > b.brand) {
                return isSosh ? -1 : 1;
            } else if (a.brand < b.brand) {
                return isSosh ? 1 : -1;
            } else {
                return 0;
            }
        });
    }

    protected constructor(
        protected activatedRoute: ActivatedRoute,
        protected errorPageService: ErrorPageService,
        protected routerService: RouterService,
        protected portfolioService: PortfolioService,
        protected kenobiService: KenobiService,
        protected tmsService: TMSService,
    ) {}

    /**
     * Replace pattern in url
     * @param {string} cid
     * @param {string} [usage=]
     * @returns {string}
     */
    public getReturnUrl(cid: string, usage = ''): string {
        // replace {{cid}} in url with selected cid
        // replace {{usage}} in url with selected usage
        const returnUrlSanitized = EcmUrlSanitizerUtil.handleAndSanitizeRoutingUri(
            this.activatedRoute.snapshot.queryParams.returnUrl,
        );
        EcmDispatcherModule.sendConsoleDebug(
            AbstractSelectorComponent.name,
            '🚀 ~ AbstractSelectorComponent ~ getReturnUrl ~ returnUrlSanitized: ' + returnUrlSanitized,
        );
        return returnUrlSanitized.replace(encodeURI('{{cid}}'), cid).replace(encodeURI('{{usage}}'), usage);
    }

    /**
     * Redirect to returnUrl
     * @param {Contract} contract
     * @param {string} [usage]
     */
    public redirectToReturnUrl(contract: Contract, usage?: string): void {
        if (!this.isReturnUrlQueryParamValid()) {
            this.errorPageService.showDefaultErrorPage();
        } else {
            this.portfolioService.setContractCookie(contract);

            const url: string = this.getReturnUrl(contract.cid, usage);

            // Tms tracking
            this.tmsService.trackLink({
                track_nom: this.universe,
                track_cible: url,
                track_page: this.universe,
                track_zone: this.universe,
            });

            if (UrlHelperPortfolio.isExternalUrl(url)) {
                this.routerService.redirectToExternalUrl(url);
            } else {
                // following is to support query params in return url
                this.routerService.navigateByUrl(url);
            }
        }
    }

    /**
     * Tracks view in monitoring services
     * @param {Status} status
     * @param {string} [functionality]
     */
    public onAfterViewLoaded(status: Status, functionality?: string): void {
        this.kenobiService.trackMulti(new KenobiParams(functionality, '*', status), IdUtils.generateRandomId(), false);

        this.tmsService.trackView({
            id_tracking: this.idTracking,
            type_page: this.universe,
            titre_page: this.universe,
            sous_univers: this.universe,
        });
    }

    /**
     * @inheritDoc
     */
    protected init() {
        this.isWebviewContext = EnvUtils.isWebview();

        if (!this.isReturnUrlQueryParamValid()) {
            // returnUrl param is not valid
            this.errorPageService.showDefaultErrorPage();
        }

        this.routerService
            .isCurrentRouteSosh()
            .subscribe((isSosh: boolean) => (this.reverseBrandLogoUrl = !isSosh ? BrandPicto.SOSH : BrandPicto.ORANGE));
    }

    /**
     * Group contracts by brand
     * @param {Contract[]} contracts
     */
    protected groupContractsByBrand(contracts: Contract[]): void {
        this.routerService.isCurrentRouteSosh().subscribe((isSosh: boolean) => {
            this.contractsByBrand = new Map<string, Contract[]>();

            AbstractSelectorComponent.sortContractsByBrand(contracts, isSosh).forEach((contract: Contract) => {
                const brand: string = contract.brand.toLowerCase();

                if (!this.contractsByBrand.has(brand)) {
                    // initialize brand
                    this.contractsByBrand.set(brand, []);
                    this.brandsAvailable.push(brand);
                }

                // group contract by brand
                this.contractsByBrand.get(brand).push(contract);
            });
        });
    }

    /**
     * Check validity of returnUrl param
     *  - Check param exist
     *  - Check returnUrl is external or not url, if external must match Orange or Sosh url
     *  @returns {boolean}
     */
    protected isReturnUrlQueryParamValid(): boolean {
        if (!Object.prototype.hasOwnProperty.call(this.activatedRoute.snapshot.queryParams, 'returnUrl')) {
            return false;
        }

        const returnUrl: string = this.activatedRoute.snapshot.queryParams.returnUrl;

        // relative urls are still in the orange/sosh domain (except for Eclair, which is still `.francetelecom.fr`)
        return (
            !EnvUtils.isProduction() || // No need to check URL if we are running on localhost
            EnvUtils.isStandalone() ||
            EnvUtils.isEclair() ||
            UrlHelper.isOrangeUrl(returnUrl) ||
            UrlHelper.isSoshUrl(returnUrl)
        );
    }
}
